import React, { useMemo } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import { Icon } from "@iconify/react";

const sanitizeValue = (val, isMulti) => {
    if (isMulti) {
        if (!Array.isArray(val)) return [];
        return val.filter(
            (v) =>
                v &&
                typeof v === "object" &&
                v.value !== undefined &&
                v.label !== undefined
        );
    } else {
        if (Array.isArray(val)) return val[0] || null;
        if (val && typeof val === "object" && val.value !== undefined) return val;
        return null;
    }
};

const CustomerSelect = ({
    value,
    onChange,
    loadCustomers,
    isMulti = false,
    placeholder = "Select Customer",
    onAddCustomer,
    showAddOption = true,
    cacheUniqs = []
}) => {
    const safeValue = useMemo(() => sanitizeValue(value, isMulti), [value, isMulti]);
    const [currentSearch, setCurrentSearch] = React.useState("");

    return (
        <AsyncPaginate
            isMulti={isMulti}
            value={safeValue}
            cacheUniqs={cacheUniqs}
            onInputChange={(search, actionMeta) => {
                if (actionMeta && actionMeta.action === "input-change") {
                    setCurrentSearch(search);
                }
            }}
            loadOptions={async (search, loadedOptions, additional) => {
                const res = await loadCustomers(search, loadedOptions, additional);
                let options = res?.options || [];

                // ➕ ADD CUSTOMER OPTION
                if (showAddOption && loadedOptions.length === 0 && !options.some(o => o.value === "__ADD_CUSTOMER__")) {
                    options.unshift({
                        value: "__ADD_CUSTOMER__",
                        label: search ? `+ Add Customer "${search}"` : "+ Add Customer",
                        searchTerm: search
                    });
                }

                return {
                    ...res,
                    options
                };
            }}
            onChange={(val) => {
                const selected = sanitizeValue(val, isMulti);
                if (isMulti) {
                    const addOpt = selected?.find(v => v.value === "__ADD_CUSTOMER__");
                    if (addOpt) {
                        if (onAddCustomer) onAddCustomer(addOpt.searchTerm || currentSearch);
                        return;
                    }
                } else {
                    if (selected?.value === "__ADD_CUSTOMER__") {
                        if (onAddCustomer) onAddCustomer(selected.searchTerm || currentSearch);
                        return;
                    }
                }
                onChange(selected);
            }}
            placeholder={placeholder}
            isClearable
            additional={{ page: 1 }}
            menuPortalTarget={document.body}
            styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base, state) => ({
                    ...base,
                    minHeight: "48px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    backgroundColor: "white",
                    boxShadow: state.isFocused ? "0 0 0 4px rgba(99, 102, 241, 0.1)" : "none",
                    borderColor: state.isFocused ? "#6366f1" : "#e2e8f0",
                    "&:hover": {
                        borderColor: "#6366f1"
                    }
                }),
                valueContainer: (base) => ({
                    ...base,
                    padding: "0 16px",
                }),
                placeholder: (base) => ({
                    ...base,
                    color: "#94a3b8",
                    fontSize: "14px"
                }),
                singleValue: (base) => ({
                    ...base,
                    color: "#1e293b",
                    fontWeight: "600",
                    fontSize: "14px"
                }),
                clearIndicator: (base) => ({
                    ...base,
                    cursor: "pointer",
                    color: "#ef4444",
                    padding: "0 8px",
                    "&:hover": {
                        color: "#dc2626",
                    }
                }),
                dropdownIndicator: (base) => ({
                    ...base,
                    color: "#94a3b8"
                }),
                indicatorSeparator: () => ({
                    display: "none"
                })
            }}
        />
    );
};

export default CustomerSelect;
